// A responsive grid whose rows always hold the same number of items (e.g. 4 → 2+2 → 1+1+1+1).
// REVIEW - might replace Group.res (to be validated)

import React, { Children, ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useResizeObserver } from 'react-aria/private/utils/useResizeObserver';
import '../theme/Spaces.stylex';
import { Gap } from '../theme/Spaces.stylex';

// MARK: exported types
export type Spacing = 'none' | 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';

// MARK: root
// ==========
type Props = {
  children: ReactNode;
  gap?: Spacing;
};

// NOTE - each column is as wide as its widest item, not as the widest item of the grid.
const requiredWidth = (itemWidths: number[], columns: number, gapWidth: number) => {
  const columnWidths = Array<number>(columns).fill(0);
  itemWidths.forEach((width, index) => {
    columnWidths[index % columns] = Math.max(columnWidths[index % columns], width);
  });
  return columnWidths.reduce((sum, width) => sum + width, 0) + (columns - 1) * gapWidth;
};

// NOTE - only divisors of the items count are candidates so that every row
// holds the same number of items (e.g. 4 items: 4, 2 + 2 or 1 + 1 + 1 + 1).
const balancedColumnsCount = (itemWidths: number[], gapWidth: number, availableWidth: number) => {
  const itemsCount = itemWidths.length;
  for (let columns = itemsCount; columns > 1; columns--) {
    if (itemsCount % columns === 0 && requiredWidth(itemWidths, columns, gapWidth) <= availableWidth) {
      return columns;
    }
  }
  return 1;
};

// NOTE - lays the items out at min-content for a synchronous read, before the browser paints.
const measureItemsMinWidths = (container: HTMLElement) => {
  container.style.gridTemplateColumns = `repeat(${container.children.length}, min-content)`;
  const widths = Array.from(container.children, item => item.getBoundingClientRect().width);
  container.style.gridTemplateColumns = '';
  return widths;
};
function Component({
  children,
  gap = 'l'
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const itemsCount = Children.toArray(children).length;
  const [columns, setColumns] = useState(itemsCount);
  const updateColumns = useCallback(() => {
    const container = ref.current;
    if (!container) return;
    const itemWidths = measureItemsMinWidths(container);
    const gapWidth = parseFloat(getComputedStyle(container).columnGap) || 0;
    setColumns(balancedColumnsCount(itemWidths, gapWidth, container.clientWidth));
  }, []);
  useResizeObserver({
    ref,
    onResize: updateColumns
  });
  useLayoutEffect(updateColumns);
  return <div ref={ref} role="presentation" {...stylex.props(styles.root, styles.columns(columns), gapStyles[gap])}>
      {children}
    </div>;
}
const MemoizedComponent = React.memo(Component);
export { MemoizedComponent as Grid };
export type { Props };

// MARK: style
// ===========
const _temp = {
  "gridTemplateColumns-kumcoG": "gridTemplateColumns-xqketvx",
  "$$css": "wino-gc:src/resources/layout-and-structure/Grid.tsx:105"
};
const styles = {
  root: {
    "display-k1xSpc": "display-xrvj5dj",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:102"
  },
  columns: (count: number) => [_temp, {
    "--x-gridTemplateColumns": `repeat(${count}, minmax(min-content, 1fr))` != null ? `repeat(${count}, minmax(min-content, 1fr))` : undefined
  }]
};
const gapStyles = {
  none: {
    "gap-kOIVth": "gap-x1t94g80",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:111"
  },
  xxs: {
    "gap-kOIVth": "gap-xqcaodp",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:112"
  },
  xs: {
    "gap-kOIVth": "gap-xw718zh",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:113"
  },
  s: {
    "gap-kOIVth": "gap-x2u8dy9",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:114"
  },
  m: {
    "gap-kOIVth": "gap-x851vlu",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:115"
  },
  l: {
    "gap-kOIVth": "gap-x7dls1a",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:116"
  },
  xl: {
    "gap-kOIVth": "gap-x1rb97w9",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:117"
  },
  xxl: {
    "gap-kOIVth": "gap-xbm0443",
    $$css: "wino-gc:src/resources/layout-and-structure/Grid.tsx:118"
  }
};